/* eslint-disable */
import { useQueryStates, parseAsArrayOf, parseAsString } from 'nuqs';
import { useCallback, useMemo } from 'react';

/**
 * View modes for multichoix search
 */
export const MULTICHOIX_VIEWS = {
  STEPS: 'steps',
  RESULTS: 'results',
};

/**
 * URL parameter parsers for multichoix search state
 * IMPORTANT: This hook stores ONLY IDs in the URL (no labels)
 * Labels are fetched via the bulk API by useMultichoixEnrichedData hook
 */
const createUrlParsers = () => ({
  view: parseAsString.withDefault(MULTICHOIX_VIEWS.STEPS),
  'treatment_cultures.culture': parseAsArrayOf(parseAsString).withDefault([]),
  'treatment_cultures.cultural_practices': parseAsArrayOf(parseAsString).withDefault([]),
  treatment_type: parseAsArrayOf(parseAsString).withDefault([]),
  'treatment_enemies.enemy': parseAsArrayOf(parseAsString).withDefault([]),
  intervention_period: parseAsArrayOf(parseAsString).withDefault([]),
  application_type: parseAsArrayOf(parseAsString).withDefault([]),
});

/**
 * URL configuration for nuqs
 */
const URL_CONFIG = {
  history: 'push',
  shallow: false,
  throttleMs: 300,
  clearOnDefault: true,
};

/**
 * Field names for selection parameters (excludes view)
 */
const SELECTION_FIELDS = [
  'treatment_cultures.culture',
  'treatment_cultures.cultural_practices',
  'treatment_type',
  'treatment_enemies.enemy',
  'intervention_period',
  'application_type',
];

/**
 * Hook for managing multichoix URL state with nuqs
 * Stores only IDs in URL - labels are fetched separately by useMultichoixEnrichedData
 *
 * @returns {Object} URL state and navigation functions
 */
export function useMultichoixURLState() {
  const urlParsers = useMemo(() => createUrlParsers(), []);

  // Use nuqs for URL state management
  const [urlState, setUrlState] = useQueryStates(urlParsers, URL_CONFIG);

  /**
   * Derive selections object (URL state without view)
   */
  const selections = useMemo(() => {
    const result = {};
    SELECTION_FIELDS.forEach((field) => {
      result[field] = urlState[field] || [];
    });
    return result;
  }, [urlState]);

  /**
   * Update a specific field in URL state
   * @param {string} field - Field name to update
   * @param {Array} values - Array of IDs to set
   */
  const updateField = useCallback(
    (field, values) => {
      if (SELECTION_FIELDS.includes(field)) {
        setUrlState({ [field]: values });
      }
    },
    [setUrlState]
  );

  /**
   * Navigate to results view with the provided selections
   * @param {Object} newSelections - Selection values to set (optional, uses current if not provided)
   */
  const goToResults = useCallback(
    (newSelections = null) => {
      if (newSelections) {
        setUrlState({
          ...newSelections,
          view: MULTICHOIX_VIEWS.RESULTS,
        });
      } else {
        setUrlState({ view: MULTICHOIX_VIEWS.RESULTS });
      }
    },
    [setUrlState]
  );

  /**
   * Navigate back to steps view
   */
  const goToSteps = useCallback(() => {
    setUrlState({ view: MULTICHOIX_VIEWS.STEPS });
  }, [setUrlState]);

  /**
   * Reset all URL state to defaults
   */
  const resetUrlState = useCallback(() => {
    const resetState = {
      view: MULTICHOIX_VIEWS.STEPS,
    };
    SELECTION_FIELDS.forEach((field) => {
      resetState[field] = [];
    });
    setUrlState(resetState);
  }, [setUrlState]);

  /**
   * Check if there are valid search parameters
   * At minimum, cultures must be selected
   * @returns {boolean} True if valid search params exist
   */
  const hasValidSearchParams = useCallback(() => {
    const cultures = urlState['treatment_cultures.culture'] || [];
    return cultures.length > 0;
  }, [urlState]);

  /**
   * Get the current URL for sharing
   * @returns {string} Current window URL
   */
  const getShareableURL = useCallback(() => {
    return window.location.href;
  }, []);

  return {
    // Raw URL state
    urlState,
    setUrlState,

    // Current view
    currentView: urlState.view,

    // Derived selections (URL state without view)
    selections,

    // Field update
    updateField,

    // Navigation
    goToResults,
    goToSteps,

    // Reset
    resetUrlState,

    // Validation
    hasValidSearchParams,

    // Sharing
    getShareableURL,
  };
}

export default useMultichoixURLState;
